import { useState, FormEvent } from 'react';
import OtpInput from '../OtpInput';
import CountdownTimer from '../CountdownTimer';
import { api } from '../../services/api';
import { VerifyOtpResponse, SendOtpResponse } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

interface OtpModalProps {
  phone: string;
  onVerified: () => Promise<void>;
  onEdit: () => void;
  onClose: () => void;
}

export default function OtpModal({ phone, onVerified, onEdit, onClose }: OtpModalProps) {
  const { t } = useLanguage();
  const [otp, setOtp] = useState<string[]>(Array(5).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('');
  const [timerKey, setTimerKey] = useState(0);

  const maskedPhone = phone.length > 4
    ? phone.slice(0, -4).replace(/./g, '*') + phone.slice(-4)
    : phone;

  const handleResend = async () => {
    try {
      await api.post<SendOtpResponse>('/api/notifications/otp/send', {
        phone,
        type: 'REGISTRATION',
      });
      setTimerKey(prev => prev + 1);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.otp.resendFailed'));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 5) {
      setError(t('auth.otp.incomplete'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      setStep(t('auth.otp.verifyingCode'));
      const verifyResult = await api.post<VerifyOtpResponse>('/api/notifications/otp/verify', {
        phone,
        otp: code,
        type: 'REGISTRATION',
      });

      if (!verifyResult.verified) {
        setError(verifyResult.message || 'Invalid OTP');
        setLoading(false);
        setStep('');
        return;
      }

      setStep(t('auth.otp.creatingAccount'));
      await onVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.otp.failed'));
    } finally {
      setLoading(false);
      setStep('');
    }
  };

  return (
    <div className="auth-modal-backdrop" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h1 className="auth-brand-text">{t('auth.brand')}</h1>
          <h2>{t('auth.otp.title')}</h2>
          <p>{t('auth.otp.subtitle')}</p>
        </div>

        <div className="otp-phone-display">
          {maskedPhone}
          <button type="button" className="otp-edit-link" onClick={onEdit}>
            {t('auth.otp.edit')}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {loading && step && <div className="step-message">{step}</div>}

        <form onSubmit={handleSubmit}>
          <OtpInput value={otp} onChange={setOtp} disabled={loading} />

          <div className="otp-timer-row">
            <CountdownTimer key={timerKey} seconds={60} onResend={handleResend} disabled={loading} />
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || otp.join('').length !== 5}
            >
              {loading ? t('auth.otp.verifying') : t('auth.next')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
